import { useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
	DiscourseValues,
	useDiscourseContext,
} from '../../Discourse/Services/DiscourseContext';
import { useLazyDraftDiscourseQuery } from '../../../graphql/graphql';
import { prepareDefaultValues } from '../../Discourse/Services/discourse.service';
import { useEffectAsync } from '../../../helpers';

export default function useMedicalReport(patientId: string) {
	const { discourseValues } = useDiscourseContext();
	const [getDraftDiscourseData] = useLazyDraftDiscourseQuery();
	const [discourseData, setDiscourseData] = useState<DiscourseValues>();

	const getDiscourseValuesFromAPI = async () => {
		const data = await getDraftDiscourseData({ patientId }).unwrap();
		if (data?.document) {
			return prepareDefaultValues(data?.document) as DiscourseValues;
		}
		return null;
	};

	useEffectAsync(async () => {
		if (discourseValues) {
			setDiscourseData(discourseValues);
		} else {
			const data: DiscourseValues | null = await getDiscourseValuesFromAPI();
			if (data) {
				setDiscourseData(data);
			}
		}
	}, []);

	const getTextFromDiscourse = async (formMethods: UseFormReturn) => {
		if (discourseData) {
			const diagnoses = [];
			if (
				discourseData.diagnosis &&
				discourseData.diagnosis?.label &&
				discourseData.diagnosis?.value
			) {
				diagnoses.push({
					value: discourseData.diagnosis?.value,
					label: discourseData.diagnosis?.label,
					diagnose: discourseData.diagnosis,
				});
			}
			if (discourseData.diagnoses) {
				discourseData.diagnoses.forEach(item => {
					if (
						item.secondaryDiagnosis?.label &&
						item.secondaryDiagnosis?.value
					) {
						diagnoses.push({
							value: item.secondaryDiagnosis?.value,
							label: item.secondaryDiagnosis?.label,
							diagnose: item.secondaryDiagnosis,
						});
					}
				});
			}
			formMethods.setValue('diagnoses', diagnoses);
			formMethods.setValue('text', discourseData?.text ?? '');
		}
	};

	const canLoadFromDiscourse = useMemo(() => {
		return !!discourseData;
	}, [discourseData]);

	return { getTextFromDiscourse, canLoadFromDiscourse };
}
